import {
  Directive,
  Input,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewContainerRef,
  NgZone,
  Inject,
  Optional,
  ChangeDetectorRef,
  TemplateRef,
  HostListener,
  WritableSignal,
  signal,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  MatTooltip,
  TooltipPosition,
  MAT_TOOLTIP_SCROLL_STRATEGY,
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltipDefaultOptions,
} from '@angular/material/tooltip';
import { TOOLTIP_POSITIONS } from '../consts/tooltip.consts';
import { Overlay, OverlayRef, ConnectedPosition, OverlayContainer } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { Platform } from '@angular/cdk/platform';
import { AriaDescriber, FocusMonitor } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import { UTooltipPanelComponent } from '../components/u-tooltip-panel/u-tooltip-panel.component';
import { TooltipOverlayContainer } from './u-tooltip-overlay.service';
import { Subject, takeUntil, timer } from 'rxjs';

export type UTooltipPosition = TooltipPosition;
export type UTooltipTrigger = 'hover' | 'click' | 'manual';

@Directive({
  selector: '[uTooltip]',
  exportAs: 'uTooltip',
  standalone: true,
  providers: [{ provide: OverlayContainer, useClass: TooltipOverlayContainer }, Overlay],
})
export class UTooltipDirective extends MatTooltip implements OnInit, OnDestroy {
  private _customTooltipRef: OverlayRef | null = null;
  private _showTimeout: any = null;
  private _hideTimeout: any = null;
  isHoverState: WritableSignal<boolean> = signal(false);

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
    scrollDispatcher: ScrollDispatcher,
    ngZone: NgZone,
    platform: Platform,
    ariaDescriber: AriaDescriber,
    focusMonitor: FocusMonitor,
    @Inject(MAT_TOOLTIP_SCROLL_STRATEGY) private scrollStrategy: any,
    @Optional() dir: Directionality,
    @Optional() @Inject(MAT_TOOLTIP_DEFAULT_OPTIONS) defaultOptions: MatTooltipDefaultOptions,
    @Inject(DOCUMENT) private document: any,
    @Optional() changeDetectorRef?: ChangeDetectorRef
  ) {
    super(
      overlay,
      elementRef,
      scrollDispatcher,
      viewContainerRef,
      ngZone,
      platform,
      ariaDescriber,
      focusMonitor,
      scrollStrategy,
      dir,
      defaultOptions,
      changeDetectorRef
    );
    (this as any)._document = this.document;
  }

  @Input('uTooltip')
  override set message(value: string) {
    super.message = value;
    if (this.isHoverState()) {
      this.show();
    }
  }
  override get message(): string {
    return super.message;
  }

  @Input('uTooltipPosition')
  override set position(value: TooltipPosition) {
    super.position = value;
  }
  override get position(): TooltipPosition {
    return super.position;
  }

  @Input('uTooltipClass')
  override set tooltipClass(value: string | string[] | Set<string> | { [key: string]: any }) {
    super.tooltipClass = value;
  }

  override get tooltipClass() {
    return super.tooltipClass;
  }

  @Input('uTooltipShowDelay')
  override set showDelay(value: number) {
    super.showDelay = value;
  }
  override get showDelay(): number {
    return super.showDelay;
  }

  @Input('uTooltipHideDelay')
  override set hideDelay(value: number) {
    super.hideDelay = value;
  }
  override get hideDelay(): number {
    return super.hideDelay;
  }

  @Input('uTooltipDisabled')
  override set disabled(value: boolean) {
    super.disabled = value;
  }
  override get disabled(): boolean {
    return super.disabled;
  }

  @Input('uTooltipEscape') escape: boolean = true;
  @Input('uTooltipTemplate') tooltipTemplate?: TemplateRef<any>;
  @Input('uTooltipTemplateContext') tooltipTemplateContext?: any;
  @Input('uTooltipTrigger') trigger: 'hover' | 'click' | 'manual' = 'hover';
  @Input('uTooltipBackdrop') hasBackdrop: boolean = false;
  @Input('uTooltipToggleText') toggleText?: { newText: string; duration: number };

  private destroy$ = new Subject<void>();
  private isCustomTooltip: boolean = false;
  private _toggleTimeout: any = null;

  ngOnChanges() {
    this.updateTooltipMode();
  }

  private updateTooltipMode() {
    this.isCustomTooltip = !this.escape || !!this.tooltipTemplate;

    if (this.isCustomTooltip) {
      super.disabled = true;
    } else {
      super.disabled = this.disabled;
    }
  }

  ngOnInit(): void {
    if (!this.tooltipClass && this.escape && !this.tooltipTemplate) {
      super.tooltipClass = 'custom-tooltip';
    }
    if (this.isCustomTooltip) {
      super.disabled = true;
    }
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.isHoverState.set(true);
    this.handleMouseEnter();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.isHoverState.set(false);
    this.handleMouseLeave();
  }

  @HostListener('click')
  onClick(): void {
    this.handleClick();
  }

  private handleMouseEnter(): void {
    if (this.trigger === 'hover') {
      this.show();
    }
  }

  private handleMouseLeave(): void {
    if (this.trigger === 'hover') {
      this.hide();
    }
  }

  private handleClick(): void {
    if (this.trigger === 'click') {
      this.toggleTooltip();
    }
    this.showTemporaryMessage();
  }

  override ngOnDestroy(): void {
    this.clearAllTimeouts();
    this.cleanup();
    super.ngOnDestroy();
  }

  private clearAllTimeouts(): void {
    this.clearShowTimeout();
    this.clearHideTimeout();
    this.clearToggleTimeout();
  }

  private clearToggleTimeout(): void {
    if (this._toggleTimeout) {
      clearTimeout(this._toggleTimeout);
      this._toggleTimeout = null;
    }
  }

  private cleanup(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.hideHtmlTooltip();
  }

  override show(delay?: number): void {
    if (this.message.trim() === '' && !this.tooltipTemplate) {
      this.hide();
      return;
    }
    if (this.isCustomTooltip) {
      this.showCustomTooltip(delay);
    } else {
      this.showMaterialTooltip(delay);
    }
  }

  private showMaterialTooltip(delay?: number): void {
    super.show(delay);
  }

  private showCustomTooltip(delay?: number): void {
    this.clearHideTimeout();

    const showDelay = delay !== undefined ? delay : this.showDelay;

    if (showDelay > 0) {
      this._showTimeout = setTimeout(() => {
        this.showHtmlTooltip();
        this._showTimeout = null;
      }, showDelay);
    } else {
      this.showHtmlTooltip();
    }
  }

  override hide(delay?: number): void {
    if (this.isCustomTooltip) {
      this.hideCustomTooltip(delay);
    } else {
      this.hideMaterialTooltip(delay);
    }
  }

  private hideMaterialTooltip(delay?: number): void {
    super.hide(delay);
  }

  private hideCustomTooltip(delay?: number): void {
    this.clearShowTimeout();

    const hideDelay = delay !== undefined ? delay : this.hideDelay;

    if (hideDelay > 0) {
      this._hideTimeout = setTimeout(() => {
        this.hideHtmlTooltip();
        this._hideTimeout = null;
      }, hideDelay);
    } else {
      this.hideHtmlTooltip();
    }
  }

  private clearShowTimeout(): void {
    if (this._showTimeout) {
      clearTimeout(this._showTimeout);
      this._showTimeout = null;
    }
  }

  private clearHideTimeout(): void {
    if (this._hideTimeout) {
      clearTimeout(this._hideTimeout);
      this._hideTimeout = null;
    }
  }

  toggleTooltip(): void {
    if (this.isCustomTooltip) {
      this.toggleCustomTooltip();
    } else {
      this.toggleMaterialTooltip();
    }
  }

  private toggleMaterialTooltip(): void {
    this.toggle();
  }

  private toggleCustomTooltip(): void {
    if (this.isTooltipVisible()) {
      this.hide();
    } else {
      this.show();
    }
  }

  private isTooltipVisible(): boolean {
    return !!(this._customTooltipRef || this._showTimeout);
  }

  showWithTarget(event?: Event, target?: HTMLElement): void {
    if (this.isCustomTooltip) {
      this.showCustomTooltipWithTarget(target);
    } else {
      this.showMaterialTooltipWithTarget();
    }
  }

  private showMaterialTooltipWithTarget(): void {
    super.show();
  }

  private showCustomTooltipWithTarget(target?: HTMLElement): void {
    if (!target) {
      this.show();
      return;
    }

    const originalElement = this.elementRef.nativeElement;
    this.temporaryChangeTarget(target);
    this.show();
    this.restoreOriginalTarget(originalElement);
  }

  private temporaryChangeTarget(target: HTMLElement): void {
    (this.elementRef as any).nativeElement = target;
  }

  private restoreOriginalTarget(originalElement: HTMLElement): void {
    (this.elementRef as any).nativeElement = originalElement;
  }

  private showHtmlTooltip(): void {
    this.hideHtmlTooltip();

    const positionStrategy = this.createPositionStrategy();
    const overlayRef = this.createOverlayRef(positionStrategy);
    const tooltipRef = this.attachTooltipComponent(overlayRef);

    this.setupTooltipContent(tooltipRef);
    this.setupBackdropHandlers(overlayRef);

    this._customTooltipRef = overlayRef;
  }

  private createPositionStrategy() {
    return this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions(this.getTooltipPositions())
      .withFlexibleDimensions(true)
      .withPush(true)
      .withViewportMargin(8);
  }

  private createOverlayRef(positionStrategy: any): OverlayRef {
    return this.overlay.create({
      hasBackdrop: this.hasBackdrop,
      backdropClass: this.hasBackdrop ? 'cdk-overlay-transparent-backdrop' : undefined,
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: this.mergeTooltipClass(),
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '50px',
    });
  }

  private attachTooltipComponent(overlayRef: OverlayRef) {
    const tooltipPortal = new ComponentPortal(UTooltipPanelComponent, this.viewContainerRef);
    return overlayRef.attach(tooltipPortal);
  }

  private setupTooltipContent(tooltipRef: any): void {
    tooltipRef.instance.content = this.message;
    tooltipRef.instance.template = this.tooltipTemplate || null;
    tooltipRef.instance.templateContext = this.tooltipTemplateContext || {};
  }

  private setupBackdropHandlers(overlayRef: OverlayRef): void {
    if (this.hasBackdrop) {
      overlayRef.backdropClick().subscribe(() => {
        this.hide();
      });

      overlayRef.keydownEvents().subscribe((event) => {
        if (event.key === 'Escape') {
          this.hide();
        }
      });
    }
  }

  private mergeTooltipClass(): string[] {
    const baseClasses = ['u-html-tooltip'];

    if (!this.tooltipClass) {
      return ['custom-tooltip', ...baseClasses];
    }

    const userClasses = this.normalizeTooltipClass(this.tooltipClass);
    return [...userClasses, ...baseClasses];
  }

  private normalizeTooltipClass(tooltipClass: string | string[] | Set<string> | { [key: string]: any }): string[] {
    if (typeof tooltipClass === 'string') {
      return [tooltipClass];
    }

    if (Array.isArray(tooltipClass)) {
      return tooltipClass;
    }

    if (tooltipClass instanceof Set) {
      return Array.from(tooltipClass);
    }

    return Object.entries(tooltipClass)
      .filter(([, isActive]) => Boolean(isActive))
      .map(([className]) => className);
  }

  private hideHtmlTooltip(): void {
    this.clearShowTimeout();
    this.disposeCustomTooltip();
  }

  private disposeCustomTooltip(): void {
    if (this._customTooltipRef) {
      this._customTooltipRef.dispose();
      this._customTooltipRef = null;
    }
  }

  private getTooltipPositions(): ConnectedPosition[] {
    return TOOLTIP_POSITIONS[this.position] || TOOLTIP_POSITIONS.above;
  }

  showTemporaryMessage(): void {
    if (!this.toggleText) {
      return;
    }

    const { newText, duration } = this.toggleText;
    const originalMessage = this.message;

    this.displayTemporaryMessage(newText, duration, originalMessage);
  }

  private displayTemporaryMessage(newText: string, duration: number, originalMessage: string): void {
    super.message = newText;
    this.show();

    timer(duration)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.hide();
        timer(150)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            super.message = originalMessage;
          });
      });
  }
}
